import { useCallback, useEffect, useState } from 'react';
import OrbitArtifactsService from '../services/OrbitArtifactsService';

export interface OrbitArtifact {
  id: number;
  client_id: number | null;
  conversation_id: string | null;
  source_message_id: number | null;
  title: string;
  artifact_type: string;
  content_markdown: string;
  preview_text: string;
  created_at: string;
  /** Set when the artifact points at a generated file (Excel / PDF) */
  orbit_file_id?: number | null;
  file?: {
    id: number;
    title: string;
    filename: string;
    file_format: string;
    status: 'building' | 'running' | 'ready' | 'error';
    error_message?: string | null;
    client_id?: number | null;
  } | null;
}

interface SaveArtifactInput {
  title?: string;
  artifact_type?: string;
  content_markdown: string;
  conversation_id?: string;
  source_message_id?: number | null;
  orbit_file_id?: number;
}

interface UpdateArtifactInput {
  title?: string;
  artifact_type?: string;
  content_markdown?: string;
}

interface UseOrbitArtifactsResult {
  artifacts: OrbitArtifact[];
  loading: boolean;
  saving: boolean;
  saveArtifact: (input: SaveArtifactInput) => Promise<void>;
  updateArtifact: (id: number, input: UpdateArtifactInput) => Promise<OrbitArtifact | undefined>;
  deleteArtifact: (id: number) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useOrbitArtifacts(clientId?: number): UseOrbitArtifactsResult {
  const [artifacts, setArtifacts] = useState<OrbitArtifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (clientId && clientId > 0) params.client_id = clientId;
      const res = await OrbitArtifactsService.list(params);
      setArtifacts(res?.data ?? []);
    } catch {
      setArtifacts([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const saveArtifact = useCallback(async (input: SaveArtifactInput) => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        ...input,
        client_id: clientId && clientId > 0 ? clientId : undefined,
      };
      const res = await OrbitArtifactsService.create(payload);
      if (res?.data) {
        setArtifacts((prev) => [res.data, ...prev]);
      } else {
        await refresh();
      }
    } finally {
      setSaving(false);
    }
  }, [clientId, refresh]);

  const updateArtifact = useCallback(async (id: number, input: UpdateArtifactInput) => {
    setSaving(true);
    try {
      const res = await OrbitArtifactsService.update(id, input);
      if (res?.data) {
        setArtifacts((prev) => prev.map((artifact) => (artifact.id === id ? res.data : artifact)));
        return res.data as OrbitArtifact;
      }
      await refresh();
      return undefined;
    } finally {
      setSaving(false);
    }
  }, [refresh]);

  const deleteArtifact = useCallback(async (id: number) => {
    await OrbitArtifactsService.delete(id);
    setArtifacts((prev) => prev.filter((artifact) => artifact.id !== id));
  }, []);

  return { artifacts, loading, saving, saveArtifact, updateArtifact, deleteArtifact, refresh };
}

